/**
 * `url` block (spec §7.4.15, M25): a web or app address as a clickable link, with no content of the
 * target. What a click does is the block's — open in a new tab or window, copy, or hand an app link to
 * the operating system — so every reader of the page gets the same behaviour.
 */
import { Check, Link as LinkIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useT } from "../../i18n";
import { useLoroSelector } from "../../sync/hooks";
import type { BlockComponentProps } from "../registry";
import { LoroTextInput, bodyMap } from "../textField";
import { checkUrl, follow, withScheme, type OpenIn, type UrlAction } from "./model";

const ACTIONS: UrlAction[] = ["browser", "copy", "system"];
const ACTION_KEYS: Record<UrlAction, string> = { browser: "urlBlock.action.browser", copy: "urlBlock.action.copy", system: "urlBlock.action.system" };

export function UrlBlock({ handle, node, readOnly, onFocus }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const state = useLoroSelector(
    handle.doc,
    () => ({
      url: String(body?.get("url") ?? ""),
      action: (["copy", "system"].includes(String(body?.get("action"))) ? body!.get("action") : "browser") as UrlAction,
      openIn: (body?.get("openIn") === "window" ? "window" : "tab") as OpenIn,
      caption: String(body?.get("caption")?.toString() ?? ""),
    }),
    (a, b) => a.url === b.url && a.action === b.action && a.openIn === b.openIn && a.caption === b.caption,
  );
  const [draft, setDraft] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  useEffect(() => {
    if (!done) return;
    const timer = setTimeout(() => setDone(null), 1800);
    return () => clearTimeout(timer);
  }, [done]);
  if (!body || !state) return null;

  const set = (key: "url" | "action" | "openIn", value: string) => {
    body.set(key, value);
    handle.doc.commit();
  };
  const commit = () => {
    if (draft === null) return;
    const next = withScheme(draft);
    if (next !== state.url) set("url", next);
    setDraft(null);
  };
  const check = checkUrl(state.url);
  const click = async (e: React.MouseEvent) => {
    if (!check.ok) return;
    // a plain click does what the block says; Ctrl/⌘-, Shift- and middle-click keep the browser's own
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    const result = await follow(check.url, state.action, state.openIn, check.web);
    if (result === "copied") setDone(t("urlBlock.copied"));
  };

  return (
    <div className="rounded-md border border-neutral-200 bg-[var(--surface)] p-2.5 shadow-[var(--shadow-sm)] dark:border-neutral-800" data-testid="url-block" data-action={state.action} data-open-in={state.openIn} onFocus={onFocus}>
      <div className="flex items-center gap-2">
        <LinkIcon size={16} className="shrink-0 text-neutral-500" aria-hidden="true" />
        {check.ok ? (
          <a
            href={check.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => void click(e)}
            className="min-w-0 truncate font-medium text-accent underline underline-offset-2"
            title={check.url}
            data-testid="url-link"
          >
            {state.caption || check.url}
          </a>
        ) : state.url ? (
          <span className="min-w-0 truncate text-sm text-err" data-testid="url-refused" data-reason={check.reason}>
            {state.url} — {t(check.reason, { scheme: check.scheme ?? "" })}
          </span>
        ) : (
          <span className="text-sm text-neutral-500">{readOnly ? t("urlBlock.none") : t("urlBlock.hint")}</span>
        )}
        {done && (
          <span className="flex shrink-0 items-center gap-1 text-xs text-ok" role="status" data-testid="url-feedback">
            <Check size={12} aria-hidden="true" /> {done}
          </span>
        )}
      </div>
      {!readOnly && (
        <input
          className="mt-2 w-full kss-control kss-field bg-transparent px-2 py-1 font-mono text-xs"
          placeholder="https://…"
          value={draft ?? state.url}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") setDraft(null);
          }}
          aria-label={t("urlBlock.address")}
          data-testid="url-input"
        />
      )}
      <LoroTextInput handle={handle} map={body} field="caption" placeholder={readOnly ? "" : t("urlBlock.caption")} readOnly={readOnly} testId="url-caption" />
      {!readOnly && (
        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-neutral-500" data-testid="url-controls">
          <label className="flex items-center gap-1">
            {t("urlBlock.whenClicked")}
            <select className="rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={state.action} data-testid="url-action" onChange={(e) => set("action", e.target.value)}>
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {t(ACTION_KEYS[a])}
                </option>
              ))}
            </select>
          </label>
          {state.action === "browser" && (
            <label className="flex items-center gap-1">
              {t("file.inA")}
              <select className="rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={state.openIn} data-testid="url-open-in" onChange={(e) => set("openIn", e.target.value)}>
                <option value="tab">{t("file.newTab")}</option>
                <option value="window">{t("file.newWindow")}</option>
              </select>
            </label>
          )}
          {state.action === "system" && <span data-testid="url-system-hint">{t("urlBlock.systemHint")}</span>}
        </div>
      )}
    </div>
  );
}
